import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Product } from "../../interfaces/productInterface";
import { fetchProducts } from "../../utils/api/apiNoAuth";

interface ProductListState {
  products: Product[];
  page: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  category: string | null;
}

const initialState: ProductListState = {
  products: [],
  page: 0,
  hasMore: true,
  loading: false,
  error: null,
  category: null
};

export const loadProducts = createAsyncThunk(
  "products/load",
  async ({ category, page }: { category?: string; page: number }, thunkAPI) => {
    const response = await fetchProducts({ category, page });
    return response.data;
  }
);

/**
 * Redux slice for managing the product list state.
 *
 * This slice handles the state for a paginated product list, including loading status,
 * error handling, category filtering, and pagination control.
 *
 * Reducers:
 * - `resetProductList`: Resets the product list, page, hasMore flag, and error state.
 * - `setCategory`: Sets the current category, resets the product list, page, and hasMore flag.
 *
 * Extra Reducers:
 * - Handles `loadProducts` async thunk for pending, fulfilled, and rejected states:
 *   - On pending: Sets loading to true and clears errors.
 *   - On fulfilled: Appends new products, updates hasMore and page, and sets loading to false.
 *   - On rejected: Sets loading to false and updates the error message.
 *
 * @module productListSlice
 */
const productListSlice = createSlice({
  name: "productList",
  initialState,
  reducers: {
    resetProductList: (state) => {
      state.products = [];
      state.page = 0;
      state.hasMore = true;
      state.error = null;
    },
    setCategory: (state, action: PayloadAction<string | null>) => {
      state.category = action.payload;
      state.products = [];
      state.page = 0;
      state.hasMore = true;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadProducts.fulfilled, (state, action) => {
        state.products.push(...action.payload);
        state.hasMore = action.payload.length === 8;
        state.page += 1;
        state.loading = false;
      })
      .addCase(loadProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Fehler beim Laden";
      });
  }
});

export const { resetProductList, setCategory } = productListSlice.actions;
export default productListSlice.reducer;